import { useCallback, useEffect, useState } from 'react';
import { readSessionFarms } from '../utils/farmSession';
const ACTIVE_LOCATION_KEY = 'ponds_active_location_id';
const LOCATION_EVENT = 'ponds:location-changed';
const readStoredLocationId = () => {
    try {
        const raw = localStorage.getItem(ACTIVE_LOCATION_KEY);
        if (!raw)
            return null;
        return raw.trim() || null;
    }
    catch {
        return null;
    }
};
const storeLocationId = (locationId) => {
    try {
        if (!locationId) {
            localStorage.removeItem(ACTIVE_LOCATION_KEY);
            return;
        }
        localStorage.setItem(ACTIVE_LOCATION_KEY, locationId);
    }
    catch {
        // no-op for storage failures
    }
};
const emitLocationChanged = (locationId) => {
    if (typeof window === 'undefined')
        return;
    window.dispatchEvent(new CustomEvent(LOCATION_EVENT, {
        detail: locationId,
    }));
};
export const usePondLocation = () => {
    const [locationId, setLocationIdState] = useState(() => {
        // Trust ponds_active_location_id from localStorage directly.
        // main/useLocations (FarmSwitcher) is the source of truth for this key —
        // it sets it on every farm selection and on initial load. Reading it here
        // synchronously prevents the null→value transition that caused double fetches
        // in usePondList and ensured ponds loaded without the correct location filter
        // when navigating from the dashboard.
        const stored = readStoredLocationId();
        if (stored !== null)
            return stored;
        // Fallback: derive from session farms for first-ever load (no stored key yet)
        const { selectedLocationId } = readSessionFarms();
        if (selectedLocationId !== null) {
            storeLocationId(selectedLocationId);
            return selectedLocationId;
        }
        return null;
    });
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const handleLocationChanged = (event) => {
            const custom = event;
            setLocationIdState(custom.detail ?? null);
        };
        const handleStorage = (event) => {
            if (event.key !== ACTIVE_LOCATION_KEY)
                return;
            setLocationIdState(readStoredLocationId());
        };
        window.addEventListener(LOCATION_EVENT, handleLocationChanged);
        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener(LOCATION_EVENT, handleLocationChanged);
            window.removeEventListener('storage', handleStorage);
        };
    }, []);
    const setLocationId = useCallback((nextLocationId) => {
        const normalized = nextLocationId?.trim() || null;
        storeLocationId(normalized);
        setLocationIdState(normalized);
        emitLocationChanged(normalized);
    }, []);
    return { locationId, setLocationId };
};
